<script setup>
/**
 * VehicleFitmentContextPanel.vue
 *
 * Displays a list of vehicle context sources for the customer intel sidebar.
 * Self-contained — intentionally does NOT import ConfidenceBadge to avoid a
 * circular dep; inline spans carry the confidence colour rules instead.
 *
 * Props:
 *   vehicleContext  Array<{source, label, confidence, note}>
 *     source:     'intake'|'garage'|'storefront'|'inferred'|'ip_only'
 *     label:      e.g. "2005 Subaru WRX STI"
 *     confidence: 'Confirmed'|'Likely'|'Possible'|'Weak'
 *     note:       optional string
 */

const props = defineProps({
  vehicleContext: {
    type: Array,
    default: () => [],
  },
});

/** Human-readable source labels */
const SOURCE_LABELS = {
  intake:     'intake form',
  garage:     'garage',
  storefront: 'storefront',
  inferred:   'inferred',
  ip_only:    'ip lookup',
};

function sourceLabel(source) {
  return SOURCE_LABELS[source] || source;
}

/**
 * Confidence badge Tailwind classes.
 * Matches the token conventions used throughout MvpCustomerIntel.vue.
 *   Confirmed  → green  (intake / garage)
 *   Likely     → blue   (storefront)
 *   Possible   → amber  (inferred)
 *   Weak       → gray   (ip_only)
 */
function confidenceClass(confidence) {
  // Neutral by default; color ONLY the exception (Weak/conflict) so amber LIVE stays the sole strong accent.
  switch (confidence) {
    case 'Weak': return 'bg-n-ruby-4 text-n-ruby-11';
    default:     return 'bg-n-solid-3 text-n-slate-11';
  }
}
</script>

<template>
  <div v-if="vehicleContext && vehicleContext.length > 0" class="space-y-1">
    <div
      v-for="(v, idx) in vehicleContext"
      :key="idx"
      class="flex flex-col gap-0.5"
    >
      <!-- Main row: icon + label + source + confidence badge -->
      <div class="flex items-center gap-1.5 flex-wrap">
        <i class="i-lucide-car text-n-slate-10 shrink-0 text-[13px]" />

        <span class="text-xs text-n-slate-12 font-medium">
          {{ v.label }}
        </span>

        <span class="text-[10px] text-n-slate-9">
          {{ sourceLabel(v.source) }}
        </span>

        <!-- Inline confidence badge (self-contained; no ConfidenceBadge import) -->
        <span
          :class="[
            'inline-flex items-center px-1.5 py-px rounded-full text-[10px] font-semibold leading-snug whitespace-nowrap',
            confidenceClass(v.confidence),
          ]"
        >
          {{ v.confidence }}
        </span>
      </div>

      <!-- Optional note on a second line -->
      <div
        v-if="v.note"
        class="pl-5 text-[10px] text-n-slate-10 leading-snug"
      >
        {{ v.note }}
      </div>
    </div>
  </div>
</template>
